import { useRef } from 'react';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import { ErrorText } from '../ErrorText';
import { formatCurrencyWhole } from '../../utils/formatCurrency';
import {
  Chip,
  ChipDetail,
  ChipPrice,
  ChipSublabel,
  ChipTime,
  ChipsRow,
  ErrorRow,
  Header,
  NavButton,
  NavControls,
  Title,
  Wrapper,
} from './TimeslotChipSelector.styles';
import type { TimeslotChipSelectorProps } from './TimeslotChipSelector.types';

export function TimeslotChipSelector({
  timeslots,
  selectedTimeslot,
  onTimeslotSelect,
  wrap = false,
  layout = 'scroll',
  labels,
  hasError,
  errorText,
}: TimeslotChipSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const title = labels?.title ?? 'Time';
  const prevLabel = labels?.previousPage ?? 'Previous timeslots';
  const nextLabel = labels?.nextPage ?? 'Next timeslots';
  const locale = labels?.locale ?? 'en-GB';

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -amount : amount });
  };

  return (
    <Wrapper>
      {!wrap && (
        <Header>
          <Title>{title}</Title>
          {layout === 'scroll' && (
            <NavControls>
              <NavButton type="button" aria-label={prevLabel} onClick={() => scroll('left')}>
                <ChevronLeftIcon size={12} />
              </NavButton>
              <NavButton type="button" aria-label={nextLabel} onClick={() => scroll('right')}>
                <ChevronRightIcon size={12} />
              </NavButton>
            </NavControls>
          )}
        </Header>
      )}
      <ChipsRow
        ref={scrollRef}
        role="listbox"
        aria-label={title}
        data-wrap={wrap || undefined}
        data-layout={wrap ? undefined : layout}
        aria-invalid={hasError || undefined}
        tabIndex={hasError ? -1 : undefined}
      >
        {timeslots.map((slot) => {
          const isSelected = slot.value === selectedTimeslot;
          return (
            <Chip
              key={slot.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              aria-disabled={slot.disabled || undefined}
              aria-label={slot.ariaLabel}
              disabled={slot.disabled}
              {...(isSelected ? { 'data-selected': '' } : undefined)}
              onClick={() => onTimeslotSelect(slot.value)}
            >
              <ChipTime $selected={isSelected}>{slot.label}</ChipTime>
              {slot.sublabel && <ChipSublabel $selected={isSelected}>{slot.sublabel}</ChipSublabel>}
              {slot.detail && <ChipDetail $selected={isSelected}>{slot.detail}</ChipDetail>}
              {slot.price != null && slot.currencyCode && (
                <ChipPrice $selected={isSelected}>
                  {formatCurrencyWhole(slot.price, slot.currencyCode, locale)}
                </ChipPrice>
              )}
            </Chip>
          );
        })}
      </ChipsRow>
      {hasError && errorText && (
        <ErrorRow>
          <ErrorText>{errorText}</ErrorText>
        </ErrorRow>
      )}
    </Wrapper>
  );
}
